/**
 * The first screen.
 *
 * Says what the product is in one sentence, shows the thing it does in one
 * figure, and offers the two things a visitor actually wants.
 *
 * **What those two things are has changed.** They used to be "install it" and
 * "read the docs", both pointing into a public repository — the shape of an
 * open-source project's page. Kimene is a commercial product, so the first
 * action is to reach the people who sell it and the second is the door for
 * somebody who already has an account. Neither invents a step that does not
 * exist: there is no sign-up here, because an account is something arranged with
 * a human.
 */
import { Reveal } from '../kit'
import { useLocale, useT } from '../i18n/context'
import { HeroDeck } from '../figures/hero/HeroDeck'
import { CONSOLE_URL } from '../data/links'
import { pathFor } from '../routes'
import './Hero.css'

export function Hero() {
  const t = useT()
  const locale = useLocale()

  return (
    <section className="kl-section kl-hero" aria-labelledby="hero-title">
      <div className="kl-inner kl-hero__inner">
        <Reveal className="kl-head kl-hero__copy">
          <p className="k-eyebrow">
            <span className="k-label">{t('hero.eyebrow')}</span>
          </p>
          <h1 id="hero-title" className="kl-hero__title">
            {t('hero.title')}
            {/* Inside the h1 so the heading is still one heading to a screen
                reader and to a crawler, and so the two halves cannot drift
                apart in the outline. `span` with its own class carries the
                typographic change; a second `h1` or an `h2` here would be a
                claim about the document's structure that is not true. */}
            <span className="kl-hero__deck">{t('hero.deck')}</span>
          </h1>
          <p className="kl-sub">{t('hero.sub')}</p>
          <div className="kl-hero__actions">
            {/* To the contact page rather than straight to a `mailto:`. A mail
                client opening from the first screen, before the reader has been
                told who they would be writing to, is a dead end with an empty
                subject line; the contact page carries the address, the telephone
                number and the request in one place. */}
            <a className="kl-btn kl-btn--primary" href={pathFor(locale, 'contact')}>
              {t('hero.ctaPrimary')}
            </a>
            <a className="kl-btn" href={CONSOLE_URL}>
              {t('hero.ctaSecondary')}
            </a>
          </div>
        </Reveal>
        <div className="kl-hero__figure k-frame">
          <HeroDeck />
        </div>
      </div>
    </section>
  )
}
